/*--external-- */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";

/*--internal-- */
import { useI18n } from "Hooks";

/**--relative--*/
import classes from "./KonvaCanvas.scss";
import { createKonvaCanvas } from "./Konva";
import KonvaContextMenu from "./KonvaContextMenu";
import {
  destroyKonvaCanvas,
  handleSelectionOnRedraw,
  addElementOverflowDragBoundary,
} from "./Konva/KonvaUtils";

/**
 *
 * @param {*} props
 * @returns Konva Canvas Component
 */
const KonvaCanvas = props => {
  const { mode, data = [], drawMode, setAttributes, worksheetMode } = props;
  const [konva, setKonva] = useState(null);
  const cavasRef = useRef();
  const previous = useRef({ mode, data, node: null });
  const konvaFields = useRef({ stage: null, layer: null, virtualLayer: null });
  const { t } = useI18n();

  const handleSetAttrs = useCallback(setAttributes, []);

  useEffect(() => {
    if (mode !== "create") {
      previous.current = { ...previous.current, node: null };
    }
  }, [mode]);

  useEffect(() => {
    (async () => {
      try {
        if (_.toNumber(cavasRef?.current?.clientWidth) < 1) return;
        if (cavasRef.current) {
          // * maintaining 16:9 aspect ration
          const canvasWidth = cavasRef.current.clientWidth;
          const canvasHeight = (canvasWidth * 9) / 16;
          cavasRef.current.style.height = `${canvasHeight}px`;
          if (_.get(konvaFields.current, "stage")) {
            // * Updating stage size
            konvaFields.current.stage.height(canvasHeight);
            konvaFields.current.stage.width(canvasWidth);
          }
        }

        const payload = {
          mode: mode,
          drawMode: drawMode,
          container: cavasRef.current,
          worksheetMode: worksheetMode,
          type: _.get(props, "type", ""),
          options: _.get(props, "options", {}),

          onDrop: _.get(props, "onDrop", () => {}),
          onDragEnd: _.get(props, "onChange", () => {}),
          onTransform: _.get(props, "onChange", () => {}),
          onDrawComplete: _.get(props, "onDrawComplete", () => {}),
          onDrawCancel: _.get(props, "onDrawCancel", () => {}),
          onTextChange: (payload, node) => {
            previous.current.mode = mode;
            previous.current.node = node;
            _.get(props, "onChange", () => {})(payload);
          },
          onClick: (...args) => {
            _.get(props, "onClick", () => {})(...args);
            if (_.first(args)) {
              previous.current.mode = mode;
              previous.current.node = _.first(args);
            }
          },
        };

        // * creating canvas instance and setting for reuse
        const instance = await createKonvaCanvas(payload, konvaFields, t);
        if (!instance) return;

        setKonva(instance);

        if (mode !== "preview") handleSetAttrs(instance);

        // * adding elements on canvas
        instance.addElements(data);

        // * Keeping transformer and context in sync
        if (mode === "create") {
          handleSelectionOnRedraw(previous, data, mode, instance);
        }

        // * Preventing element(IMAGE/TEXT) to completely drag out of canvas
        addElementOverflowDragBoundary(instance);
      } catch (error) {}
    })();

    return () => destroyKonvaCanvas(konva);
  }, [
    mode,
    worksheetMode,
    handleSetAttrs,
    JSON.stringify(data),
    JSON.stringify(drawMode),
    JSON.stringify(_.omit(_.get(props, "options", {}), ["findHotspotAnswers"])),
  ]);

  const isCreateMode = konva && mode === "create" && !_.isEmpty(data);
  const showContextMenu = !_.get(drawMode, "isDrawing") && isCreateMode;

  const containerId = `${_.get(props, "fieldId", "")}-${mode}`;

  return (
    <>
      <div
        id={`konva-canvas-container-id-${containerId}`}
        style={{ width: _.get(props, "width", "100%") }}
        className={classes.canvasContainer}
        ref={cavasRef}
      />
      {showContextMenu && _.get(props, "contextMenu", null) && konva && (
        <KonvaContextMenu
          konva={konva}
          fieldId={_.get(props, "fieldId", "")}
          onRemove={_.get(props, "onRemove", () => {})}
          onDuplicate={_.get(props, "onDuplicate", () => {})}
          contextMenu={_.get(props, "contextMenu", null)}
        />
      )}
      {_.get(props, "extraElements", () => null)(data, mode, konva)}
    </>
  );
};

export default KonvaCanvas;

KonvaCanvas.defaultProps = {
  data: [],
  options: {},
  drawMode: {},
  width: "100%",
  questionId: "",
  mode: "preview",
  type: "dragAndDrop",
  onDrop: () => {},
  onClick: () => {},
  onChange: () => {},
  contextMenu: null,
  onDrawCancel: () => {},
  setAttributes: () => {},
  extraElements: () => {},
  onDrawComplete: () => {},
};

KonvaCanvas.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  options: PropTypes.object,
  drawMode: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionId: PropTypes.string,
  mode: PropTypes.string,
  type: PropTypes.string,
  onDrop: PropTypes.func,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  contextMenu: PropTypes.func,
  onDrawCancel: PropTypes.func,
  setAttributes: PropTypes.func,
  extraElements: PropTypes.func,
  onDrawComplete: PropTypes.func,
};
